import React, { useState, useEffect, useCallback } from 'react'
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import Loader from '../../../components/common/Loader';
import Nodata from '../../../components/common/NoData';
import { usePermissions } from '../../../context/PermissionsContext';
import {
  getClassroomAssignmentQuestions,
  addClassroomAssignmentQuestion,
  updateClassroomAssignmentQuestion,
  deleteClassroomAssignmentQuestion
 } from '../../../services/adminService';

function ClassroomAssignmentResources({decodedClassroomId, decodedAssignmentId}) {
  const { permissions } = usePermissions();
  // console.log('decodedClassroomId',decodedClassroomId);
  // console.log('decodedAssignmentId',decodedAssignmentId);

  // State for questions/resources
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [questionToEdit, setQuestionToEdit] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    questionText: '',
    questionType: 'text',
    responseType: 'text_response',
    mediaFile: null,
    marks: 0,
    existingMediaUrl: null,
    removeMedia: false
  });
  const [formErrors, setFormErrors] = useState({});

  // Media preview state
  const [mediaPreview, setMediaPreview] = useState(null);
  const [showMediaPreview, setShowMediaPreview] = useState(false);

  // Fetch questions function
  const fetchQuestions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getClassroomAssignmentQuestions({
        assignment_id: decodedAssignmentId,
        class_id: decodedClassroomId,
        page: page,
        limit: limit,
        search: searchTerm
      });

      // console.log("Get questions response:", response);

      if (response && response.success) {
        setQuestions(response.data.questions || []);
        setTotalQuestions(response.data.pagination?.totalRecords || 0);
        setTotalPages(response.data.pagination?.totalPages || 1);
      } else {
        toast.error(response?.message || 'Failed to fetch questions');
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('An error occurred while fetching questions');
    } finally {
      setIsLoading(false);
    }
  }, [decodedAssignmentId, decodedClassroomId, page, limit, searchTerm]);

  // Fetch questions on component mount
  useEffect(() => {
    if (decodedAssignmentId && decodedClassroomId) {
      fetchQuestions();
    }
  }, [decodedAssignmentId, decodedClassroomId, fetchQuestions]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Reset media when question type changes to text
    if (name === 'questionType' && value === 'text') {
      setMediaPreview(null);
      setShowMediaPreview(false);
      setFormData(prev => ({
        ...prev,
        mediaFile: null,
        existingMediaUrl: null,
        removeMedia: false
      }));
    }
  };

  // Handle media file changes
  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFormData(prev => ({
      ...prev,
      mediaFile: file,
      removeMedia: false
    }));

    // Create preview
    const fileType = file.type.split('/')[0];
    const reader = new FileReader();

    reader.onloadend = () => {
      setMediaPreview({
        type: fileType,
        url: reader.result
      });
      setShowMediaPreview(true);
    };

    if (file) {
      reader.readAsDataURL(file);
    }
  };

  // Handle removing media
  const handleRemoveMedia = () => {
    if (isEditMode && formData.existingMediaUrl) {
      setFormData(prev => ({
        ...prev,
        removeMedia: true,
        mediaFile: null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        mediaFile: null
      }));
    }

    setMediaPreview(null);
    setShowMediaPreview(false);
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.questionText.trim()) {
      errors.questionText = 'Question text is required';
    }

    if (!formData.questionType) {
      errors.questionType = 'Question type is required';
    }

    if (!formData.responseType) {
      errors.responseType = 'Response type is required';
    }

    // Check if media file is required and provided
    if (formData.questionType === 'image' || formData.questionType === 'audio') {
      const hasExistingMedia = isEditMode && formData.existingMediaUrl && !formData.removeMedia;
      if (!hasExistingMedia && !formData.mediaFile) {
        errors.mediaFile = `${formData.questionType === 'image' ? 'Image' : 'Audio'} file is required`;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstError = Object.values(formErrors)[0];
      if (firstError) {
        toast.error(firstError);
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();

      formDataToSend.append('class_id', decodedClassroomId);
      formDataToSend.append('assignment_id', decodedAssignmentId);
      formDataToSend.append('question_type', formData.questionType);
      formDataToSend.append('question_text', formData.questionText);
      formDataToSend.append('response_type', formData.responseType);
      formDataToSend.append('marks', Number(formData.marks || 0));

      if (isEditMode && questionToEdit) {
        formDataToSend.append('question_id', questionToEdit.id);
      }

      // Handle media
      if (formData.mediaFile) {
        formDataToSend.append('media', formData.mediaFile);
      } else if (isEditMode && formData.existingMediaUrl && !formData.removeMedia) {
        formDataToSend.append('existing_media_url', formData.existingMediaUrl);
      } else if (isEditMode && formData.removeMedia) {
        formDataToSend.append('remove_media', 'true');
      }

      let response;
      if (isEditMode && questionToEdit) {
        response = await updateClassroomAssignmentQuestion(formDataToSend);
      } else {
        response = await addClassroomAssignmentQuestion(formDataToSend);
      }

      if (response && response.success) {
        toast.success(isEditMode ? 'Question updated successfully' : 'Question added successfully');
        handleModalClose();
        fetchQuestions();
      } else {
        toast.error(response?.message || `Failed to ${isEditMode ? 'update' : 'add'} question`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} question:`, error);
      toast.error(`An error occurred while ${isEditMode ? 'updating' : 'adding'} the question`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      questionText: '',
      questionType: 'text',
      responseType: 'text_response',
      mediaFile: null,
      marks: 0,
      existingMediaUrl: null,
      removeMedia: false
    });
    setFormErrors({});
    setIsEditMode(false);
    setQuestionToEdit(null);
    setMediaPreview(null);
    setShowMediaPreview(false);
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowModal(false);
    resetForm();
  };

  // Handle modal show
  const handleModalShow = () => {
    if (!permissions.classroom_assignment_resource_create) {
      toast.warning("You don't have permission to create resources");
      return;
    }
    resetForm();
    setShowModal(true);
  };

  // Handle edit question
  const handleEdit = (question) => {
    if (!permissions.classroom_assignment_resource_edit) {
      toast.warning("You don't have permission to edit resources");
      return;
    }
    setIsEditMode(true);
    setQuestionToEdit(question);

    setFormData({
      questionText: question.question_text || '',
      questionType: question.question_type || 'text',
      responseType: question.response_type || 'text_response',
      marks: question.marks || 0,
      mediaFile: null,
      existingMediaUrl: question.media_url || null,
      removeMedia: false
    });

    // Set media preview if there's a media URL
    if (question.media_url) {
      if (question.question_type === 'image') {
        setMediaPreview({
          type: 'image',
          url: question.media_url
        });
      } else if (question.question_type === 'audio') {
        setMediaPreview({
          type: 'audio',
          url: question.media_url
        });
      }
      setShowMediaPreview(true);
    } else {
      setShowMediaPreview(false);
      setMediaPreview(null);
    }

    setShowModal(true);
  };

  // Handle delete question
  const handleDelete = (question) => {
    if (!permissions.classroom_assignment_resource_delete) {
      toast.warning("You don't have permission to delete resources");
      return;
    }
    setQuestionToDelete(question);
    setShowDeleteModal(true);
  };

  // Confirm delete question
  const confirmDeleteQuestion = async () => {
    if (!questionToDelete) return;

    setIsDeleting(true);
    try {
      const response = await deleteClassroomAssignmentQuestion({
        assignment_id: decodedAssignmentId,
        class_id: decodedClassroomId,
        question_id: questionToDelete.id
      });

      if (response && response.success) {
        toast.success('Question deleted successfully');
        fetchQuestions();
      } else {
        toast.error(response?.message || 'Failed to delete question');
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('An error occurred while deleting the question');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setQuestionToDelete(null);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Handle limit change
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  return (
    <>
      {/* add the search and add resources  */}
      <div className="flex mb-3">
        <div className="w-full md:w-1/2 mb-2">
          <input
            type="text"
            className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Search Resource"
            style={{ width: '300px' }}
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <div className="w-full md:w-1/2 flex justify-end mb-2">
          <button
            type="button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed min-w-[160px]"
            onClick={handleModalShow}
            disabled={!permissions.classroom_assignment_resource_create}
            title={!permissions.classroom_assignment_resource_create ? "You don't have permission to create resources" : "Add Resource"}
          >
            <Icon icon="fluent:add-24-regular" width="20" height="20" />
            Add Resource
          </button>
        </div>
      </div>

      {/* Add Resource Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h5 className="font-semibold text-lg">{isEditMode ? 'Edit Resource' : 'Add Resource'}</h5>
              <button
                type="button"
                className="p-1 rounded hover:bg-gray-100"
                onClick={handleModalClose}
              >
                <Icon icon="mdi:close" width="20" height="20" />
              </button>
            </div>
            <div className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Question Type*</label>
              <select
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                  formErrors.questionType ? 'border-red-500' : 'border-input-border'
                }`}
                name="questionType"
                value={formData.questionType}
                onChange={handleInputChange}
                disabled={isEditMode}
              >
                <option value="">Select Question Type</option>
                <option value="text">Text Question</option>
                <option value="image">Image Question</option>
                <option value="audio">Audio Question</option>
              </select>
              {formErrors.questionType && (
                <div className="text-red-500 text-sm mt-1">{formErrors.questionType}</div>
              )}
              {isEditMode && (
                <small className="text-gray-500">Question type cannot be changed when editing</small>
              )}
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Question Text*</label>
              <textarea
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                  formErrors.questionText ? 'border-red-500' : 'border-input-border'
                }`}
                rows="3"
                name="questionText"
                value={formData.questionText}
                onChange={handleInputChange}
                placeholder="Enter your question here"
              />
              {formErrors.questionText && (
                <div className="text-red-500 text-sm mt-1">{formErrors.questionText}</div>
              )}
            </div>

            {formData.questionType !== 'text' && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {formData.questionType === 'image' ? 'Upload Image' : 'Upload Audio'}
                  {(isEditMode && formData.existingMediaUrl && !formData.removeMedia) ? '' : '*'}
                </label>

                {/* Show file input if no existing media or it's being removed */}
                {(!isEditMode || !formData.existingMediaUrl || formData.removeMedia) && (
                  <>
                    <input
                      type="file"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                        formErrors.mediaFile ? 'border-red-500' : 'border-input-border'
                      }`}
                      onChange={handleMediaChange}
                      accept={formData.questionType === 'image' ? 'image/*' : 'audio/*'}
                    />
                    {formErrors.mediaFile && (
                      <div className="text-red-500 text-sm mt-1">{formErrors.mediaFile}</div>
                    )}
                    <small className="text-gray-500">
                      {formData.questionType === 'image'
                        ? 'Supported formats: JPG, PNG, GIF (Max 5MB)'
                        : 'Supported formats: MP3, WAV (Max 10MB)'}
                    </small>
                  </>
                )}
              </div>
            )}

            {/* Media Preview */}
            {(showMediaPreview && (mediaPreview || formData.existingMediaUrl)) && (
              <div className="mb-3">
                {formData.questionType === 'image' ? (
                  <div className="border border-gray-200 rounded p-2 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-light">Image Preview</span>
                      <button
                        type="button"
                        className="border border-red-500 text-red-500 hover:bg-red-50 font-medium py-1 px-3 rounded-lg text-sm"
                        onClick={handleRemoveMedia}
                      >
                        <Icon icon="fluent:delete-24-regular" className="mr-1" />
                        Remove
                      </button>
                    </div>
                    <img
                      src={mediaPreview?.url || formData.existingMediaUrl}
                      alt="Question preview"
                      className="w-full rounded"
                      style={{ maxHeight: '200px' }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                    <audio
                      controls
                      className="flex-grow-1"
                      style={{ height: '40px', minWidth: '300px' }}
                    >
                      <source src={mediaPreview?.url || formData.existingMediaUrl} type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                    <button
                      type="button"
                      className="border border-red-500 text-red-500 hover:bg-red-50 font-medium py-1 px-3 rounded-lg text-sm"
                      onClick={handleRemoveMedia}
                      title="Remove audio"
                    >
                      <Icon icon="fluent:delete-24-regular" />
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Response Type*</label>
              <select
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                  formErrors.responseType ? 'border-red-500' : 'border-input-border'
                }`}
                name="responseType"
                value={formData.responseType}
                onChange={handleInputChange}
              >
                <option value="">Select Response Type</option>
                <option value="text_response">Text Response</option>
                <option value="image_response">Image Response</option>
                <option value="audio_response">Audio Response</option>
              </select>
              {formErrors.responseType && (
                <div className="text-red-500 text-sm mt-1">{formErrors.responseType}</div>
              )}
              <small className="text-gray-500">
                Select the type of response you expect from trainees
              </small>
            </div>

            <div className="flex flex-col gap-2">
              <button
                type="button"
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg border border-gray-300"
                style={{ padding: '12px' }}
                onClick={handleModalClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
                style={{ padding: '12px' }}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" role="status" aria-hidden="true"></span>
                    {isEditMode ? 'Updating...' : 'Adding...'}
                  </>
                ) : (
                  isEditMode ? 'Update Resource' : 'Add Resource'
                )}
              </button>
            </div>
          </form>
            </div>
          </div>
        </div>
      )}

      {/* table  */}
      <div className="flex mb-3">
        <div className="w-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            {isLoading ? (
              <div className="flex justify-center my-5">
                <Loader />
              </div>
            ) : questions.length === 0 ? (
              <div className="flex justify-center my-5">
                <Nodata message="No resources found" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse mb-0">
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '80px' }}>SL. No</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Resource</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Resource Type</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Response Type</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((question, index) => (
                      <tr key={question.id} className="border-bottom">
                        <td className="py-3 align-middle">{(page - 1) * limit + index + 1}</td>
                        <td className="py-3 align-middle">
                          <div className="truncate" style={{ maxWidth: '300px' }} title={question.question_text}>
                            {question.question_text}
                          </div>
                        </td>
                        <td className="py-3 align-middle">
                          {question.question_type === 'image' && question.media_url ? (
                            <div className="flex items-center">
                              <Icon icon="fluent:image-24-regular" className="text-primary mr-2" />
                              <span>Image</span>
                      
                            </div>
                          ) : question.question_type === 'audio' && question.media_url ? (
                            <div className="flex items-center">
                              <Icon icon="fluent:speaker-2-24-regular" className="text-green-500 mr-2" />
                              <span>Audio</span>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <Icon icon="fluent:text-24-regular" className="text-gray-500 mr-2" />
                              <span>Text</span>
                            </div>
                          )}
                        </td>
                        <td className="py-3 align-middle">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                            {question.response_type === 'text_response' ? 'Text' :
                             question.response_type === 'image_response' ? 'Image' :
                             question.response_type === 'audio_response' ? 'Audio' :
                             question.response_type}
                          </span>
                        </td>
                        <td className="py-3 align-middle">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-primary text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={!permissions.classroom_assignment_resource_edit ? "You don't have permission to edit resources" : "Edit Resource"}
                              onClick={() => handleEdit(question)}
                              disabled={!permissions.classroom_assignment_resource_edit}
                            >
                              <Icon icon="fluent:edit-24-regular" width="18" height="18" />
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-red-500 text-red-500 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={!permissions.classroom_assignment_resource_delete ? "You don't have permission to delete resources" : "Delete Resource"}
                              onClick={() => handleDelete(question)}
                              disabled={!permissions.classroom_assignment_resource_delete}
                            >
                              <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* record per page and pagination  */}
      {!isLoading && questions.length > 0 && (
        <div className="flex mt-4 pt-4 border-t border-gray-100">
          <div className="md:w-1/2 flex flex-row items-center">
            <select
              className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              style={{ width: 'auto' }}
              value={limit}
              onChange={handleLimitChange}
            >
              <option value={5}>5 records per page</option>
              <option value={10}>10 records per page</option>
              <option value={25}>25 records per page</option>
              <option value={50}>50 records per page</option>
            </select>
            <small className="text-gray-500 ml-2">
              Total {totalQuestions} records
            </small>
          </div>
          <div className="md:w-1/2 flex justify-end items-center gap-3">
            <button
              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
              style={{ width: '150px' }}
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              Prev
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
              style={{ width: '150px' }}
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h5 className="font-semibold text-lg text-red-500">Confirm Deletion</h5>
              <button
                type="button"
                className="p-1 rounded hover:bg-gray-100"
                onClick={() => {
                  setShowDeleteModal(false);
                  setQuestionToDelete(null);
                }}
              >
                <Icon icon="mdi:close" width="20" height="20" />
              </button>
            </div>
            <div className="p-6">
              <p>Are you sure you want to delete this resource?</p>
              {questionToDelete && (
                <p className="mb-0"><strong>Resource:</strong> {questionToDelete.question_text}</p>
              )}
              <p className="text-red-500 mb-0 mt-2">This action cannot be undone.</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100">
              <div className="flex flex-col gap-2 w-full">
                <button
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg border border-gray-300"
                  style={{ padding: '12px' }}
                  onClick={() => {
                    setShowDeleteModal(false);
                    setQuestionToDelete(null);
                  }}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg"
                  style={{ padding: '12px' }}
                  onClick={confirmDeleteQuestion}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : (
                    'Delete Resource'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ClassroomAssignmentResources
